"use client"

import type React from "react"
import { useAuth } from "../contexts/AuthContext"
import { Sparkles } from "lucide-react"
import { useRouter } from "next/navigation"
import { useState, useEffect } from "react"
import { analytics } from "../lib/firebase"
import { logEvent }    from "firebase/analytics"

// Use Record<string, never> for an empty props object
type CTABannerProps = Record<string, never>

const CTABanner: React.FC<CTABannerProps> = () => {
  const { user } = useAuth()
  const router = useRouter()
  const [hasAllSchoolsAccess, setHasAllSchoolsAccess] = useState(false)

  // Check if the user has all schools access
  useEffect(() => {
    const checkAllSchoolsAccess = async () => {
      if (!user) {
        setHasAllSchoolsAccess(false)
        return
      }

      try {
        // Use a dummy school ID to check for all_schools access
        const dummySchoolId = "dummy-school-id"
        const response = await fetch(`/api/check-school-access?user_id=${user.uid}&school_id=${dummySchoolId}`)
        const data = await response.json()

        // If the user has access and it's of type "all_schools", they have all schools access
        setHasAllSchoolsAccess(data.hasAccess && data.accessType === "all_schools")
      } catch (error) {
        console.error("Error checking all schools access:", error)
        setHasAllSchoolsAccess(false)
      }
    }

    checkAllSchoolsAccess()
  }, [user])

  // Only hide the banner if the user is logged in AND has all schools access
  if (user && hasAllSchoolsAccess) {
    return null
  }

  // Always redirect to benefits page, regardless of login status
  const handleBannerClick = () => {
    if (analytics) {
      logEvent(analytics, "click_banner_99kr", {
        page: "school_search",
        }
      )
    }
    router.push("/benefits?all_schools=true")
  }

  // Also update the handleButtonClick function to use the same logic
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent the banner's onClick from firing
    handleBannerClick()
  }

  return (
    <div
      onClick={handleBannerClick}
      className="relative overflow-hidden bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-lg p-5 mb-8 transform transition-all hover:scale-[1.01] duration-300 cursor-pointer"
      role="button"
      aria-label="Köp tillgång till all information"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleBannerClick()
        }
      }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-6 -mt-6"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-white opacity-10 rounded-full -ml-4 -mb-4"></div>

      <div className="flex flex-col md:flex-row justify-between items-center relative z-10">
        <div className="flex items-center">
          <Sparkles className="h-8 w-8 text-yellow-200 mr-3 animate-pulse" />
          <div>
            <h3 className="text-lg md:text-xl font-extrabold text-white mb-1">Få tillgång till ALL information!</h3>
            <p className="text-yellow-100 font-medium text-sm md:text-base">För endast <span className="text-red-600 font-bold">99kr</span> <span className="line-through">(149kr)</span> får du tillgång till detaljerad information om alla skolor i en månad.</p>
          </div>
        </div>
        <div className="mt-4 md:mt-0 md:ml-6 flex-shrink-0">
          <div className="relative">
            <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-bounce">
              Bästa erbjudandet!
            </div>
            <button
              onClick={handleButtonClick}
              className="bg-white hover:bg-yellow-100 text-orange-600 font-extrabold py-3 px-6 rounded-lg text-lg shadow-md transform transition-transform hover:-translate-y-1"
            >
              Köp nu!
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CTABanner

